import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ScrollReveal from "@/components/ScrollReveal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", message: "", enquiry_type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", company: "", phone: "", email: "", message: "", enquiry_type: "" });
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--sb-blue)] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal>
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-orange)] mb-4 block">Reach Out</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6">Contact Us</h1>
            <p className="text-blue-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Whether you're hiring or looking for a job, we'd love to hear from you. Get in touch and let's start a conversation.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section data-testid="contact-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Cards */}
            <div className="lg:col-span-2 space-y-6">
              <ScrollReveal>
                <div className="bg-slate-50 border border-slate-200 p-8">
                  <Phone className="w-8 h-8 text-[var(--sb-blue)] mb-4" />
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">Phone</h4>
                  <a href="tel:+919214463185" className="text-slate-600 text-sm hover:text-[var(--sb-blue)] transition-colors block">+91 92144 63185</a>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={1}>
                <div className="bg-slate-50 border border-slate-200 p-8">
                  <Mail className="w-8 h-8 text-[var(--sb-blue)] mb-4" />
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">Email</h4>
                  <a href="mailto:info@skillsbridge.co.in" className="text-slate-600 text-sm hover:text-[var(--sb-blue)] transition-colors block">info@skillsbridge.co.in</a>
                  <a href="mailto:hr@skillsbridge.co.in" className="text-slate-600 text-sm hover:text-[var(--sb-blue)] transition-colors block mt-1">hr@skillsbridge.co.in</a>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={2}>
                <div className="bg-slate-50 border border-slate-200 p-8">
                  <MapPin className="w-8 h-8 text-[var(--sb-blue)] mb-4" />
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">Address</h4>
                  <p className="text-slate-600 text-sm">India</p>
                </div>
              </ScrollReveal>

              {/* Map Placeholder */}
              <ScrollReveal delay={3}>
                <div className="bg-slate-100 border border-slate-200 h-48 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Google Maps embed placeholder</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <ScrollReveal>
                  <div className="bg-white border border-[var(--sb-green)] p-12 text-center">
                    <CheckCircle2 className="w-16 h-16 text-[var(--sb-green)] mx-auto mb-4" />
                    <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-2">Message Sent!</h3>
                    <p className="text-slate-600">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <button onClick={() => setSubmitted(false)} data-testid="contact-new-message-btn" className="mt-6 text-sm font-bold uppercase tracking-wider text-[var(--sb-blue)] hover:underline">
                      Send Another Message
                    </button>
                  </div>
                </ScrollReveal>
              ) : (
                <ScrollReveal>
                  <div className="bg-white border border-slate-200 p-8 md:p-10">
                    <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-6">Send Us a Message</h3>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Name *</label>
                          <Input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Company</label>
                          <Input data-testid="contact-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Phone</label>
                          <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Email *</label>
                          <Input data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Enquiry Type</label>
                        <Select value={form.enquiry_type} onValueChange={(val) => setForm({ ...form, enquiry_type: val })}>
                          <SelectTrigger data-testid="contact-enquiry-type-select" className="bg-slate-50 border-slate-200 rounded-none">
                            <SelectValue placeholder="Select Enquiry Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Hiring">Hiring</SelectItem>
                            <SelectItem value="Job Seeker">Job Seeker</SelectItem>
                            <SelectItem value="General">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Message *</label>
                        <Textarea data-testid="contact-message-textarea" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" placeholder="How can we help you?" />
                      </div>
                      <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="bg-[var(--sb-blue)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[var(--sb-blue-hover)] transition-all hover:-translate-y-1 inline-flex items-center gap-2 disabled:opacity-50">
                        {submitting ? "Sending..." : "Send Message"} <ArrowRight className="w-5 h-5" />
                      </button>
                    </form>
                  </div>
                </ScrollReveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
